import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, Chip, CircularProgress, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import ApiService from "@/api/ApiService";

// Reuse your existing helpers from the service feature
import { generateBins, getBinDefinitons, getSubtitle, BobEvent } from "@/shared/sharedService/helpers";
// If your BobEvent type lives elsewhere, import from there

// lucide icons (reuse your RenderIcon approach but for service categories/bins)
import {
  Ambulance,
  ChevronsDown,
  ChevronsUp,
  ClipboardList,
  Clock,
  ClockAlert,
  ListChecks,
  MessageCircle,
  Plus,
  SquareArrowOutUpRight,
  Stethoscope,
  Wrench,
} from "lucide-react";
import { Search } from "@mui/icons-material";
import StyledModal from "@/components/planning/dialogs/newMaterialOrder/StyledModal";
import EventSidebar from "@/shared/sharedService/EventSidebar";
import useRights from "@/shared/api/useRights";
import EventEditModal, { EventRef } from "@/shared/sharedService/EventEditModal";
import DeviceMenu, { DeviceTabType } from "./DeviceMenu";

export function explainEventSource(source: string): string {
  switch (source) {
    case "service_region":
      return "Du siehst diesen Eintrag, weil du die Werkstattberechtigung hast.";

    case "staff_bookings_window":
      return "Du siehst diesen Eintrag, weil du in einem relevanten Zeitraum auf einer Baustelle mit diesem Gerät eingeplant bist.";

    case "kleingeraete_staff":
      return "Du siehst diesen Eintrag, weil du das Kleingerät zuletzt erfasst oder damit gearbeitet hast.";

    case "user_messages":
      return "Du siehst diesen Eintrag, weil du einen Kommentar hinterlassen hast.";

    case "manager_resource_bookings":
      return "Du siehst diesen Eintrag, weil du Bauleiter oder Vertreter einer Baustelle bist, auf der das Gerät eingeplant ist.";

    case "kleingeraete_manager":
      return "Du siehst diesen Eintrag, weil du Bauleiter oder Vertreter einer Baustelle bist, dem dieses Kleingerät zuletzt zugeordnet war.";

    default:
      return "Du siehst diesen Eintrag aufgrund deiner aktuellen Berechtigungen.";
  }
}

function categoryIcon(category?: string) {
  switch (category) {
    case "external":
      return <SquareArrowOutUpRight />;
    case "meterReading":
      return <Clock />;
    case "testing":
      return <ListChecks />;
    case "maintenance":
      return <Stethoscope />;
    case "damage":
      return <Ambulance />;
    default:
      return <ClipboardList />;
  }
}

function PriorityIcon({ priority }: { priority?: "low" | "normal" | "high" }) {
  if (priority === "high") return <ChevronsUp />;
  if (priority === "low") return <ChevronsDown />;
  return null;
}

type Props = {
  deviceId: number;
  deviceNumber: string;
  readonly: boolean;
  setVisiblePage: (page: string) => void;
};

export default function DeviceService({ deviceId, image, readOnly, deviceNumber, setVisiblePage }: Props) {
  const navigate = useNavigate();

  const query = useQuery<BobEvent[], any, BobEvent[]>({
    queryKey: ["getDeviceEvents", deviceId],
    queryFn: ApiService.getEntitiesWithFilter("bobEvent", { resource: ["resource", deviceId ?? 0] }, "resource"),
    enabled: (deviceId ?? 0) > 0,
  });

  const deviceQuery = useQuery<any[], any, any | null>({
    queryKey: ["getDeviceByNumber", deviceNumber],
    queryFn: ApiService.getEntitiesWithFilter("resource", { number: deviceNumber }, "details"),
    select: (response) => (!!response && response.length > 0 ? response[0] : null),
  });

  const events = query.data ?? [];

  const [searchText, setSearchText] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");

  const [sidebarId, setSidebarId] = useState<number | null>(null);
  const sidebarOpen = sidebarId !== null;

  const rights = useRights();

  const handleOpenSidebar = (id: number) => {
    setSidebarId(id);
  };
  const handleCloseSidebar = () => {
    setSidebarId(null);
  };

  const modalRef = React.useRef<EventRef>(null);

  const [categoryToggles, setCategoryToggles] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedFilter(searchText), 300);
    return () => window.clearTimeout(handle);
  }, [searchText]);

  // 1) Only text search first
  const searchedData = useMemo(() => {
    const term = debouncedFilter.trim().toLowerCase();
    if (!term) return events;

    return events.filter((event) => {
      const parts: string[] = [];

      parts.push(event.title ?? "", event.categorySettings?.title ?? "", event.category ?? "");

      const lastBooking = event.resource?.lastBooking;
      if (lastBooking && lastBooking[1]) {
        const [kind, payload] = lastBooking as any;

        if (kind === "rb") {
          parts.push(payload?.job?.phase?.project?.number ?? "", payload?.job?.phase?.project?.name ?? "");
        } else if (kind === "spb") {
          parts.push(payload?.specialPlace?.name ?? "");
        } else if (kind === "kgp") {
          parts.push(payload?.project?.number ?? "", payload?.project?.name ?? "");
        }
      }

      return parts.join(" ").toLowerCase().includes(term);
    });
  }, [events, debouncedFilter]);

  // 2) categories present after search (before category filter)
  const availableCategories = useMemo(() => {
    const map = new Map<string, string>();

    for (const e of searchedData) {
      const key = e.category;
      if (!key || ["testing", "meterReading"].includes(e.category)) continue;
      if (!map.has(key)) map.set(key, e.categorySettings?.title ?? key);
    }

    const knownOrder = ["external", "meterReading", "testing", "maintenance", "damage"];

    const entries = Array.from(map.entries()).map(([key, label]) => ({ key, label }));

    entries.sort((a, b) => {
      const ai = knownOrder.indexOf(a.key);
      const bi = knownOrder.indexOf(b.key);
      const aKnown = ai !== -1;
      const bKnown = bi !== -1;

      if (aKnown && bKnown) return ai - bi;
      if (aKnown) return -1;
      if (bKnown) return 1;
      return a.label.localeCompare(b.label);
    });

    return entries;
  }, [searchedData]);

  // 3) auto-enable newly appearing categories
  useEffect(() => {
    if (availableCategories.length === 0) return;

    setCategoryToggles((prev) => {
      let changed = false;
      const next = { ...prev };

      for (const { key } of availableCategories) {
        if (next[key] === undefined) {
          next[key] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [availableCategories]);

  // 4) category filter
  const categoryFilteredData = useMemo(() => {
    if (availableCategories.length === 0) return searchedData;

    return searchedData.filter((e) => {
      const enabled = categoryToggles[e.category];
      return enabled !== false;
    });
  }, [searchedData, categoryToggles, availableCategories.length]);

  // 5) wartungen split, same logic
  const { filteredEvents, wartungen } = useMemo(() => {
    return categoryFilteredData.reduce(
      (acc, item) => {
        if (item.category === "testing" || item.category === "meterReading") {
          const isOverdue =
            !item.completedAt && item.assignedTo === "service" && !!item.dueAt && dayjs(item.dueAt, "YYYY-MM-DD") <= dayjs().add(4, "days");

          if (isOverdue) acc.wartungen.push(item);
        } else {
          acc.filteredEvents.push(item);
        }
        return acc;
      },
      { filteredEvents: [], wartungen: [] } as { filteredEvents: BobEvent[]; wartungen: BobEvent[] },
    );
  }, [categoryFilteredData]);

  const bins = useMemo(() => {
    if (!filteredEvents) return [];
    return generateBins(filteredEvents, getBinDefinitons("service", "global"), true);
  }, [filteredEvents]);

  const isEmpty = !query.isLoading && wartungen.length === 0 && bins.length === 0;

  return (
    <>
      <DeviceMenu
        type={"SERVICE"}
        image={image}
        setType={setVisiblePage}
        plusButton={
          readOnly ? undefined : (
            <Button
              onClick={() => modalRef.current?.openModal(null, "full")}
              variant="contained"
              size="small"
              sx={{
                minWidth: 0,
                bgcolor: "common.white",
                color: "common.black",
                border: "1px solid",
                borderColor: "grey.300",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "grey.100",
                  boxShadow: "none",
                  borderColor: "grey.400",
                },
              }}
            >
              <Plus />
            </Button>
          )
        }
      />
      <Box
        sx={{
          display: "flex",
          border: "1px solid #aaa",
          flexDirection: "column",
          position: "relative",
          flex: 1,
          minHeight: 0,
          borderBottomLeftRadius: 1,
          borderBottomRightRadius: 1,
          borderTopWidth: 0,
        }}
      >
        {/* Filters on top */}

        <Box sx={{ borderBottom: "1px solid #aaa" }}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            size="medium"
            sx={{ p: 1 }}
            variant="standard"
            fullWidth
            placeholder="Filtern..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          {!query.isLoading && availableCategories.length > 0 ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", p: 1, borderTop: "1px solid #aaa" }}>
              {availableCategories.map(({ key, label }) => {
                const isOn = categoryToggles[key] !== false;

                return (
                  <Chip
                    key={key}
                    label={(isOn ? "\u2611" : "\u2610") + " " + label}
                    onClick={() => setCategoryToggles((prev) => ({ ...prev, [key]: !(prev[key] !== false) }))}
                    variant={!isOn ? "filled" : "outlined"}
                    sx={{ mb: 1 }}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Box>

        {/* Scroll area below */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {query.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : query.isError ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
              <Typography color="error" sx={{ mt: 2 }}>
                Fehler beim Laden der Service Events.
              </Typography>
            </Box>
          ) : isEmpty ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
              <Typography>Nichts gefunden</Typography>
            </Box>
          ) : (
            <Box>
              {/* Bins */}
              {bins.map((bin) => {
                const BinIcon = bin.icon as any;

                return (
                  <Box key={bin.key} sx={{}}>
                    <div className="notificationDate" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ display: "inline-flex", alignItems: "center" }}>
                        <BinIcon size={18} />
                      </span>
                      <span>{bin.name}</span>
                    </div>

                    {bin.events.map((event: BobEvent) => (
                      <div key={event.id} className={"overduebox notificationbox servicebox"} onClick={() => handleOpenSidebar(event.id)}>
                        <div
                          className={
                            "obcolor color " +
                            (event.priority === "low" || !!event.completedAt ? "lightgrey" : event.priority === "high" ? "red" : "blue")
                          }
                        >
                          <Tooltip title={explainEventSource(event.source ?? "")}>{categoryIcon(event.category)}</Tooltip>
                        </div>

                        <div className="obfacts">
                          <div className="obbl">{event.title || event.categorySettings?.title}</div>

                          {/* last booking info (mirrors the app display roughly, but compact for overduebox) */}
                          {event.resource?.lastBooking && event.resource.lastBooking[1] ? (
                            <div className="obbl">
                              {(() => {
                                const [kind, payload] = event.resource.lastBooking as any;
                                if (kind === "rb") return `${payload.job?.phase?.project?.number} ${payload.job?.phase?.project?.name}`;
                                if (kind === "spb") return `${payload.specialPlace?.name}`;
                                return `${payload.project?.number} ${payload.project?.name}`;
                              })()}
                            </div>
                          ) : null}

                          {/* Keep original subtitle logic */}
                          {getSubtitle ? (
                            <div className="obdate" style={{ opacity: 0.9 }}>
                              {getSubtitle(event, true)}
                            </div>
                          ) : null}
                        </div>
                        <div style={{ display: "flex", padding: 3, alignItems: "center", gap: 3 }}>
                          <PriorityIcon priority={event.priority as any} />
                          <MessageCircle size={16} />
                          <span style={{ fontSize: 12, opacity: 0.9 }}>{event.messageCount ?? 0}</span>
                        </div>
                      </div>
                    ))}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
        <StyledModal open={sidebarOpen} onClose={handleCloseSidebar} onShow={() => {}}>
          {!!sidebarId ? (
            <EventSidebar
              dateOnlyScheduling
              allowMessageDeletion={"DISPOSITION" in rights}
              modalMode
              id={sidebarId}
              onCreate={setSidebarId}
              onClose={handleCloseSidebar}
              apiService={ApiService}
              deviceNumber={0}
              fullAppRights={"DISPOSITION" in rights || "SERVICE" in rights}
              role={"SERVICE" in rights ? "service" : "site"}
            />
          ) : null}
        </StyledModal>
        {!!deviceQuery.data ? (
          <EventEditModal
            dateOnlyScheduling
            ApiService={ApiService}
            role={"service"}
            isFPM={false}
            noHours={false}
            onCreate={setSidebarId}
            ref={modalRef}
            testingContent={
              deviceQuery.data.testingContent || deviceQuery.data.primaryResourceTag.testingContent
                ? (deviceQuery.data.testingContent ?? []).concat(deviceQuery.data.primaryResourceTag.testingContent ?? [])
                : null
            }
            lastReading={deviceQuery.data.lastMeterReading?.runningHours || null}
            deviceId={deviceQuery.data.id}
            deviceNumber={deviceQuery.data.number}
          />
        ) : null}
      </Box>
    </>
  );
}
